import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAiSettings, getImageSettings, getStockMediaSettings, getVoiceSettings, updateAiSettings, updateImageSettings, updateStockMediaSettings, updateVoiceSettings } from '../../services/contentAiService';

function clampNumber(v, { min, max, fallback }) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function safeJsonParse(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  try {
    const v = JSON.parse(s);
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

function prettyJson(v) {
  try {
    return JSON.stringify(v ?? null, null, 2);
  } catch {
    return '';
  }
}

const TEXT_PROVIDERS = ['openai', 'claude', 'gemini', 'deepseek'];
const IMAGE_PROVIDERS = ['dalle', 'gemini'];
const VOICE_PROVIDERS = ['elevenlabs', 'playht'];

function coerceProvider(value, allowed, fallback) {
  const normalized = String(value || '').trim().toLowerCase();
  return allowed.includes(normalized) ? normalized : fallback;
}

export default function AiSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [textTab, setTextTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [imageSettings, setImageSettings] = useState(null);
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [stockSettings, setStockSettings] = useState(null);

  const [draft, setDraft] = useState({
    preferredProvider: 'openai',
    fallbackEnabled: true,
    timeoutMs: 8000,
    maxOutputTokens: 400,
    temperature: 0.4,
    rateLimitRpm: 60,
    concurrencyLimit: 5,
    cacheEnabled: true,
    cacheTtlSeconds: 3600,
    monthlyBudgetUsd: '',
    dailyTokenCap: '',
    openaiBaseUrl: 'https://api.openai.com',
    openaiModel: 'gpt-4o-mini',
    ollamaBaseUrl: 'http://host.docker.internal:11434',
    ollamaModel: 'llama3.1',
    anthropicModel: 'claude-3-5-sonnet-latest',
    geminiModel: 'gemini-2.5-flash',
    deepseekBaseUrl: '',
    deepseekModel: 'deepseek-chat'
  });

  const [imageDraft, setImageDraft] = useState({
    preferredProvider: 'dalle',
    fallbackEnabled: true,
    rateLimitRpm: 60,
    concurrencyLimit: 3,
    cacheEnabled: true,
    cacheTtlSeconds: 86400,
    stableDiffusionBaseUrl: '',
    sdxlBaseUrl: '',
    fluxBaseUrl: '',
    openaiImageModel: 'dall-e-3',
    providerPriorityText: ''
  });

  const [voiceDraft, setVoiceDraft] = useState({
    preferredProvider: 'elevenlabs',
    fallbackEnabled: true,
    rateLimitRpm: 60,
    concurrencyLimit: 3,
    cacheEnabled: true,
    cacheTtlSeconds: 86400,
    elevenlabsModel: '',
    playhtUserId: '',
    playhtVoiceEngine: '',
    xttsBaseUrl: 'http://host.docker.internal:5002',
    coquiBaseUrl: '',
    kokoroBaseUrl: '',
    kokoroApiKey: ''
  });

  const [stockDraft, setStockDraft] = useState({
    enabled: true
  });

  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [deepseekApiKey, setDeepseekApiKey] = useState('');

  const [pexelsApiKey, setPexelsApiKey] = useState('');
  const [pixabayApiKey, setPixabayApiKey] = useState('');
  const [clearPexelsKey, setClearPexelsKey] = useState(false);
  const [clearPixabayKey, setClearPixabayKey] = useState(false);

  const load = useCallback(async () => {
    try {
      const [aiResp, imgResp, voiceResp, stockResp] = await Promise.all([
        getAiSettings().catch(() => null),
        getImageSettings().catch(() => null),
        getVoiceSettings().catch(() => null),
        getStockMediaSettings().catch(() => null)
      ]);

      const data = aiResp?.data || null;
      setSettings(data);
      if (data) {
        setDraft((prev) => ({
          ...prev,
          preferredProvider: coerceProvider(data.preferredProvider, TEXT_PROVIDERS, prev.preferredProvider),
          fallbackEnabled: data.fallbackEnabled !== false,
          timeoutMs: Number(data.timeoutMs ?? prev.timeoutMs),
          maxOutputTokens: Number(data.maxOutputTokens ?? prev.maxOutputTokens),
          temperature: Number(data.temperature ?? prev.temperature),
          rateLimitRpm: Number(data.rateLimitRpm ?? prev.rateLimitRpm),
          concurrencyLimit: Number(data.concurrencyLimit ?? prev.concurrencyLimit),
          cacheEnabled: data.cacheEnabled !== false,
          cacheTtlSeconds: Number(data.cacheTtlSeconds ?? prev.cacheTtlSeconds),
          monthlyBudgetUsd: data.monthlyBudgetUsd != null ? String(data.monthlyBudgetUsd) : '',
          dailyTokenCap: data.dailyTokenCap != null ? String(data.dailyTokenCap) : '',
          openaiBaseUrl: data.openaiBaseUrl || prev.openaiBaseUrl,
          openaiModel: data.openaiModel || prev.openaiModel,
          ollamaBaseUrl: data.ollamaBaseUrl || prev.ollamaBaseUrl,
          ollamaModel: data.ollamaModel || prev.ollamaModel,
          anthropicModel: data.anthropicModel || prev.anthropicModel,
          geminiModel: data.geminiModel || prev.geminiModel,
          deepseekBaseUrl: data.deepseekBaseUrl || '',
          deepseekModel: data.deepseekModel || prev.deepseekModel
        }));
      }

      const img = imgResp?.data || null;
      setImageSettings(img);
      if (img) {
        setImageDraft((prev) => ({
          ...prev,
          preferredProvider: coerceProvider(img.preferredProvider, IMAGE_PROVIDERS, prev.preferredProvider),
          fallbackEnabled: img.fallbackEnabled !== false,
          rateLimitRpm: Number(img.rateLimitRpm ?? prev.rateLimitRpm),
          concurrencyLimit: Number(img.concurrencyLimit ?? prev.concurrencyLimit),
          cacheEnabled: img.cacheEnabled !== false,
          cacheTtlSeconds: Number(img.cacheTtlSeconds ?? prev.cacheTtlSeconds),
          stableDiffusionBaseUrl: img.stableDiffusionBaseUrl || '',
          sdxlBaseUrl: img.sdxlBaseUrl || '',
          fluxBaseUrl: img.fluxBaseUrl || '',
          openaiImageModel: img.openaiImageModel || prev.openaiImageModel,
          providerPriorityText: img.providerPriority ? prettyJson(img.providerPriority) : ''
        }));
      }

      const v = voiceResp?.data || null;
      setVoiceSettings(v);
      if (v) {
        setVoiceDraft((prev) => ({
          ...prev,
          preferredProvider: coerceProvider(v.preferredProvider, VOICE_PROVIDERS, prev.preferredProvider),
          fallbackEnabled: v.fallbackEnabled !== false,
          rateLimitRpm: Number(v.rateLimitRpm ?? prev.rateLimitRpm),
          concurrencyLimit: Number(v.concurrencyLimit ?? prev.concurrencyLimit),
          cacheEnabled: v.cacheEnabled !== false,
          cacheTtlSeconds: Number(v.cacheTtlSeconds ?? prev.cacheTtlSeconds),
          elevenlabsModel: v.elevenlabsModel || '',
          playhtUserId: v.playhtUserId || '',
          playhtVoiceEngine: v.playhtVoiceEngine || '',
          xttsBaseUrl: v.xttsBaseUrl || prev.xttsBaseUrl,
          coquiBaseUrl: v.coquiBaseUrl || '',
          kokoroBaseUrl: v.kokoroBaseUrl || '',
          kokoroApiKey: ''
        }));
      }

      const stock = stockResp?.data || null;
      setStockSettings(stock);
      if (stock) {
        setStockDraft((prev) => ({
          ...prev,
          enabled: stock.enabled !== false
        }));
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar configurações de IA');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveText = async () => {
    setLoading(true);
    try {
      const payload = {
        preferredProvider: draft.preferredProvider,
        fallbackEnabled: draft.fallbackEnabled === true,
        timeoutMs: clampNumber(draft.timeoutMs, { min: 1000, max: 3600000, fallback: 8000 }),
        maxOutputTokens: clampNumber(draft.maxOutputTokens, { min: 16, max: 4000, fallback: 400 }),
        temperature: clampNumber(draft.temperature, { min: 0, max: 2, fallback: 0.4 }),
        rateLimitRpm: clampNumber(draft.rateLimitRpm, { min: 1, max: 3000, fallback: 60 }),
        concurrencyLimit: clampNumber(draft.concurrencyLimit, { min: 1, max: 100, fallback: 5 }),
        cacheEnabled: draft.cacheEnabled === true,
        cacheTtlSeconds: clampNumber(draft.cacheTtlSeconds, { min: 0, max: 7 * 24 * 3600, fallback: 3600 }),
        openaiBaseUrl: draft.openaiBaseUrl || null,
        openaiModel: draft.openaiModel || null,
        ollamaBaseUrl: draft.ollamaBaseUrl || null,
        ollamaModel: draft.ollamaModel || null,
        anthropicModel: draft.anthropicModel || null,
        geminiModel: draft.geminiModel || null,
        deepseekBaseUrl: draft.deepseekBaseUrl || null,
        deepseekModel: draft.deepseekModel || null,
        monthlyBudgetUsd: String(draft.monthlyBudgetUsd || '').trim() ? Number(draft.monthlyBudgetUsd) : null,
        dailyTokenCap: String(draft.dailyTokenCap || '').trim() ? Number(draft.dailyTokenCap) : null
      };

      if (openaiApiKey.trim()) payload.openaiApiKey = openaiApiKey.trim();
      if (anthropicApiKey.trim()) payload.anthropicApiKey = anthropicApiKey.trim();
      if (geminiApiKey.trim()) payload.geminiApiKey = geminiApiKey.trim();
      if (deepseekApiKey.trim()) payload.deepseekApiKey = deepseekApiKey.trim();

      await updateAiSettings(payload);
      setOpenaiApiKey('');
      setAnthropicApiKey('');
      setGeminiApiKey('');
      setDeepseekApiKey('');
      await load();
      toast.success('Configurações de IA salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar configurações de IA');
    } finally {
      setLoading(false);
    }
  };

  const saveImage = async () => {
    setLoading(true);
    try {
      const parsedPriority = safeJsonParse(imageDraft.providerPriorityText);
      const payload = {
        preferredProvider: imageDraft.preferredProvider,
        fallbackEnabled: imageDraft.fallbackEnabled === true,
        rateLimitRpm: clampNumber(imageDraft.rateLimitRpm, { min: 1, max: 3000, fallback: 60 }),
        concurrencyLimit: clampNumber(imageDraft.concurrencyLimit, { min: 1, max: 100, fallback: 3 }),
        cacheEnabled: imageDraft.cacheEnabled === true,
        cacheTtlSeconds: clampNumber(imageDraft.cacheTtlSeconds, { min: 0, max: 7 * 24 * 3600, fallback: 86400 }),
        stableDiffusionBaseUrl: imageDraft.stableDiffusionBaseUrl || null,
        sdxlBaseUrl: imageDraft.sdxlBaseUrl || null,
        fluxBaseUrl: imageDraft.fluxBaseUrl || null,
        openaiImageModel: imageDraft.openaiImageModel || null,
        providerPriority: parsedPriority || null
      };
      await updateImageSettings(payload);
      await load();
      toast.success('Configurações de Imagens salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar configurações de Imagens');
    } finally {
      setLoading(false);
    }
  };

  const saveVoice = async () => {
    setLoading(true);
    try {
      const payload = {
        preferredProvider: voiceDraft.preferredProvider,
        fallbackEnabled: voiceDraft.fallbackEnabled === true,
        rateLimitRpm: clampNumber(voiceDraft.rateLimitRpm, { min: 1, max: 3000, fallback: 60 }),
        concurrencyLimit: clampNumber(voiceDraft.concurrencyLimit, { min: 1, max: 100, fallback: 3 }),
        cacheEnabled: voiceDraft.cacheEnabled === true,
        cacheTtlSeconds: clampNumber(voiceDraft.cacheTtlSeconds, { min: 0, max: 7 * 24 * 3600, fallback: 86400 }),
        elevenlabsModel: voiceDraft.elevenlabsModel || null,
        playhtUserId: voiceDraft.playhtUserId || null,
        playhtVoiceEngine: voiceDraft.playhtVoiceEngine || null,
        xttsBaseUrl: voiceDraft.xttsBaseUrl || null,
        coquiBaseUrl: voiceDraft.coquiBaseUrl || null,
        kokoroBaseUrl: voiceDraft.kokoroBaseUrl || null
      };
      if (String(voiceDraft.kokoroApiKey || '').trim()) payload.kokoroApiKey = String(voiceDraft.kokoroApiKey).trim();
      await updateVoiceSettings(payload);
      setVoiceDraft((prev) => ({ ...prev, kokoroApiKey: '' }));
      await load();
      toast.success('Configurações de Voz salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar configurações de Voz');
    } finally {
      setLoading(false);
    }
  };

  const saveStock = async () => {
    setLoading(true);
    try {
      const payload = { enabled: stockDraft.enabled === true };
      if (clearPexelsKey) payload.pexelsApiKey = '';
      else if (pexelsApiKey.trim()) payload.pexelsApiKey = pexelsApiKey.trim();
      if (clearPixabayKey) payload.pixabayApiKey = '';
      else if (pixabayApiKey.trim()) payload.pixabayApiKey = pixabayApiKey.trim();
      await updateStockMediaSettings(payload);
      setPexelsApiKey('');
      setPixabayApiKey('');
      setClearPexelsKey(false);
      setClearPixabayKey(false);
      await load();
      toast.success('Configurações de Stock salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar configurações de Stock');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (tab === 1) return saveImage();
    if (tab === 2) return saveVoice();
    if (tab === 3) return saveStock();
    if (tab === 4) return load();
    return saveText();
  };

  const providers = [
    { key: 'openai', label: 'OpenAI' },
    { key: 'claude', label: 'Claude (Anthropic)' },
    { key: 'gemini', label: 'Gemini (Google)' },
    { key: 'deepseek', label: 'DeepSeek' }
  ];

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">IA · Configurações</Typography>
        <Button variant="contained" onClick={save} disabled={loading}>{tab === 4 ? 'Atualizar' : 'Salvar'}</Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Texto" />
        <Tab label="Imagens" />
        <Tab label="Voz" />
        <Tab label="Stock" />
        <Tab label="Runtime" />
      </Tabs>

      {tab === 0 ? (
        <Box display="grid" gap={2}>
          <Tabs value={textTab} onChange={(_, v) => setTextTab(v)} variant="scrollable" allowScrollButtonsMobile>
            <Tab label="Geral" />
            <Tab label="Providers" />
          </Tabs>

          {textTab === 0 ? (
            <Box display="grid" gap={2} maxWidth={820}>
              <FormControl fullWidth>
                <InputLabel id="ai-provider-label">Provider</InputLabel>
                <Select
                  labelId="ai-provider-label"
                  label="Provider"
                  value={draft.preferredProvider}
                  onChange={(e) => setDraft((prev) => ({ ...prev, preferredProvider: e.target.value }))}
                >
                  {providers.map((p) => (
                    <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch checked={draft.fallbackEnabled === true} onChange={(e) => setDraft((prev) => ({ ...prev, fallbackEnabled: e.target.checked }))} />
                  <Typography variant="body2">Fallback</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch checked={draft.cacheEnabled === true} onChange={(e) => setDraft((prev) => ({ ...prev, cacheEnabled: e.target.checked }))} />
                  <Typography variant="body2">Cache</Typography>
                </Box>
              </Box>

              <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                <TextField
                  label="Timeout (ms)"
                  value={draft.timeoutMs}
                  onChange={(e) => setDraft((prev) => ({ ...prev, timeoutMs: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Max output tokens"
                  value={draft.maxOutputTokens}
                  onChange={(e) => setDraft((prev) => ({ ...prev, maxOutputTokens: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Temperature"
                  value={draft.temperature}
                  onChange={(e) => setDraft((prev) => ({ ...prev, temperature: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Rate limit (RPM)"
                  value={draft.rateLimitRpm}
                  onChange={(e) => setDraft((prev) => ({ ...prev, rateLimitRpm: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Concurrency"
                  value={draft.concurrencyLimit}
                  onChange={(e) => setDraft((prev) => ({ ...prev, concurrencyLimit: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Cache TTL (s)"
                  value={draft.cacheTtlSeconds}
                  onChange={(e) => setDraft((prev) => ({ ...prev, cacheTtlSeconds: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Orçamento mensal (USD)"
                  value={draft.monthlyBudgetUsd}
                  onChange={(e) => setDraft((prev) => ({ ...prev, monthlyBudgetUsd: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Limite diário (tokens)"
                  value={draft.dailyTokenCap}
                  onChange={(e) => setDraft((prev) => ({ ...prev, dailyTokenCap: e.target.value }))}
                  fullWidth
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                {settings ? `Atualizado: ${settings.updatedAt ? new Date(settings.updatedAt).toLocaleString() : ''}` : ''}
              </Typography>
            </Box>
          ) : null}

          {textTab === 1 ? (
            <Box display="grid" gap={3} maxWidth={980}>
              <Box display="grid" gap={2}>
                <Typography variant="h6">OpenAI</Typography>
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                  <TextField label="Base URL" value={draft.openaiBaseUrl} onChange={(e) => setDraft((p) => ({ ...p, openaiBaseUrl: e.target.value }))} fullWidth />
                  <TextField label="Model" value={draft.openaiModel} onChange={(e) => setDraft((p) => ({ ...p, openaiModel: e.target.value }))} fullWidth />
                  <TextField label="API Key (somente para atualizar)" type="password" value={openaiApiKey} onChange={(e) => setOpenaiApiKey(e.target.value)} fullWidth />
                </Box>
              </Box>

              <Box display="grid" gap={2}>
                <Typography variant="h6">Anthropic (Claude)</Typography>
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                  <TextField label="Model" value={draft.anthropicModel} onChange={(e) => setDraft((p) => ({ ...p, anthropicModel: e.target.value }))} fullWidth />
                  <TextField label="API Key (somente para atualizar)" type="password" value={anthropicApiKey} onChange={(e) => setAnthropicApiKey(e.target.value)} fullWidth />
                </Box>
              </Box>

              <Box display="grid" gap={2}>
                <Typography variant="h6">Google (Gemini)</Typography>
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                  <TextField label="Model" value={draft.geminiModel} onChange={(e) => setDraft((p) => ({ ...p, geminiModel: e.target.value }))} fullWidth />
                  <TextField label="API Key (somente para atualizar)" type="password" value={geminiApiKey} onChange={(e) => setGeminiApiKey(e.target.value)} fullWidth />
                </Box>
              </Box>

              <Box display="grid" gap={2}>
                <Typography variant="h6">DeepSeek</Typography>
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                  <TextField label="Base URL" value={draft.deepseekBaseUrl} onChange={(e) => setDraft((p) => ({ ...p, deepseekBaseUrl: e.target.value }))} fullWidth />
                  <TextField label="Model" value={draft.deepseekModel} onChange={(e) => setDraft((p) => ({ ...p, deepseekModel: e.target.value }))} fullWidth />
                  <TextField label="API Key (somente para atualizar)" type="password" value={deepseekApiKey} onChange={(e) => setDeepseekApiKey(e.target.value)} fullWidth />
                </Box>
              </Box>

            </Box>
          ) : null}
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box display="grid" gap={2} maxWidth={980}>
          <FormControl fullWidth>
            <InputLabel id="img-provider-label">Provider</InputLabel>
            <Select
              labelId="img-provider-label"
              label="Provider"
              value={imageDraft.preferredProvider}
              onChange={(e) => setImageDraft((prev) => ({ ...prev, preferredProvider: e.target.value }))}
            >
              <MenuItem value="dalle">OpenAI (DALL·E)</MenuItem>
              <MenuItem value="gemini">Gemini (Imagen)</MenuItem>
            </Select>
          </FormControl>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={imageDraft.fallbackEnabled === true} onChange={(e) => setImageDraft((prev) => ({ ...prev, fallbackEnabled: e.target.checked }))} />
              <Typography variant="body2">Fallback</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={imageDraft.cacheEnabled === true} onChange={(e) => setImageDraft((prev) => ({ ...prev, cacheEnabled: e.target.checked }))} />
              <Typography variant="body2">Cache</Typography>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
            <TextField label="OpenAI image model" value={imageDraft.openaiImageModel} onChange={(e) => setImageDraft((p) => ({ ...p, openaiImageModel: e.target.value }))} fullWidth />
            <TextField label="Rate limit (RPM)" value={imageDraft.rateLimitRpm} onChange={(e) => setImageDraft((p) => ({ ...p, rateLimitRpm: e.target.value }))} fullWidth />
            <TextField label="Concurrency" value={imageDraft.concurrencyLimit} onChange={(e) => setImageDraft((p) => ({ ...p, concurrencyLimit: e.target.value }))} fullWidth />
            <TextField label="Cache TTL (s)" value={imageDraft.cacheTtlSeconds} onChange={(e) => setImageDraft((p) => ({ ...p, cacheTtlSeconds: e.target.value }))} fullWidth />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Providers locais de imagem foram desativados nesta fase de limpeza e não são mais expostos na interface.
          </Typography>

          <TextField
            label="Provider priority (JSON) (opcional)"
            value={imageDraft.providerPriorityText}
            onChange={(e) => setImageDraft((p) => ({ ...p, providerPriorityText: e.target.value }))}
            minRows={6}
            multiline
            fullWidth
          />

          <Typography variant="body2" color="text.secondary">
            {imageSettings ? `Atualizado: ${imageSettings.updatedAt ? new Date(imageSettings.updatedAt).toLocaleString() : ''}` : ''}
          </Typography>
        </Box>
      ) : null}

      {tab === 2 ? (
        <Box display="grid" gap={2} maxWidth={980}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Clone e cadastre vozes na tela dedicada de vozes.
            </Typography>
            <Button component={RouterLink} to="/voice-ai" variant="outlined">
              Abrir Vozes IA
            </Button>
          </Box>
          <FormControl fullWidth>
            <InputLabel id="voice-provider-label">Provider</InputLabel>
            <Select
              labelId="voice-provider-label"
              label="Provider"
              value={voiceDraft.preferredProvider}
              onChange={(e) => setVoiceDraft((prev) => ({ ...prev, preferredProvider: e.target.value }))}
            >
              <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
              <MenuItem value="playht">PlayHT</MenuItem>
            </Select>
          </FormControl>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={voiceDraft.fallbackEnabled === true} onChange={(e) => setVoiceDraft((prev) => ({ ...prev, fallbackEnabled: e.target.checked }))} />
              <Typography variant="body2">Fallback</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={voiceDraft.cacheEnabled === true} onChange={(e) => setVoiceDraft((prev) => ({ ...prev, cacheEnabled: e.target.checked }))} />
              <Typography variant="body2">Cache</Typography>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
            <TextField label="ElevenLabs model" value={voiceDraft.elevenlabsModel} onChange={(e) => setVoiceDraft((p) => ({ ...p, elevenlabsModel: e.target.value }))} fullWidth />
            <TextField label="PlayHT userId" value={voiceDraft.playhtUserId} onChange={(e) => setVoiceDraft((p) => ({ ...p, playhtUserId: e.target.value }))} fullWidth />
            <TextField label="PlayHT voice engine" value={voiceDraft.playhtVoiceEngine} onChange={(e) => setVoiceDraft((p) => ({ ...p, playhtVoiceEngine: e.target.value }))} fullWidth />
            <TextField label="Rate limit (RPM)" value={voiceDraft.rateLimitRpm} onChange={(e) => setVoiceDraft((p) => ({ ...p, rateLimitRpm: e.target.value }))} fullWidth />
            <TextField label="Concurrency" value={voiceDraft.concurrencyLimit} onChange={(e) => setVoiceDraft((p) => ({ ...p, concurrencyLimit: e.target.value }))} fullWidth />
            <TextField label="Cache TTL (s)" value={voiceDraft.cacheTtlSeconds} onChange={(e) => setVoiceDraft((p) => ({ ...p, cacheTtlSeconds: e.target.value }))} fullWidth />
          </Box>

          <Typography variant="body2" color="text.secondary">
            XTTS, Coqui, Kokoro e demais opções locais de TTS foram desativados nesta fase de limpeza.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {voiceSettings ? `Atualizado: ${voiceSettings.updatedAt ? new Date(voiceSettings.updatedAt).toLocaleString() : ''}` : ''}
          </Typography>
        </Box>
      ) : null}

      {tab === 3 ? (
        <Box display="grid" gap={2} maxWidth={980}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="h6">Stock Media (Pexels / Pixabay)</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={stockDraft.enabled === true} onChange={(e) => setStockDraft((prev) => ({ ...prev, enabled: e.target.checked }))} />
              <Typography variant="body2">Ativo</Typography>
            </Box>
          </Box>

          <Box display="grid" gap={1}>
            <TextField
              label="PEXELS_API_KEY"
              type="password"
              value={pexelsApiKey}
              onChange={(e) => { setPexelsApiKey(e.target.value); setClearPexelsKey(false); }}
              placeholder={stockSettings?.pexelsKeyPreview ? stockSettings.pexelsKeyPreview : ''}
              helperText={`Cadastrada: ${stockSettings?.hasPexelsKey ? 'sim' : 'não'} · Fonte: ${stockSettings?.keySource?.pexels || '-'}${clearPexelsKey ? ' · Será removida ao salvar' : ''}`}
              fullWidth
            />
            <Button variant="outlined" color="warning" onClick={() => { setPexelsApiKey(''); setClearPexelsKey(true); }} disabled={loading || !(stockSettings?.hasPexelsKey)}>
              Limpar chave Pexels
            </Button>
          </Box>

          <Box display="grid" gap={1}>
            <TextField
              label="PIXABAY_API_KEY"
              type="password"
              value={pixabayApiKey}
              onChange={(e) => { setPixabayApiKey(e.target.value); setClearPixabayKey(false); }}
              placeholder={stockSettings?.pixabayKeyPreview ? stockSettings.pixabayKeyPreview : ''}
              helperText={`Cadastrada: ${stockSettings?.hasPixabayKey ? 'sim' : 'não'} · Fonte: ${stockSettings?.keySource?.pixabay || '-'}${clearPixabayKey ? ' · Será removida ao salvar' : ''}`}
              fullWidth
            />
            <Button variant="outlined" color="warning" onClick={() => { setPixabayApiKey(''); setClearPixabayKey(true); }} disabled={loading || !(stockSettings?.hasPixabayKey)}>
              Limpar chave Pixabay
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary">
            As chaves são armazenadas criptografadas no banco. Se você deixar os campos vazios e não clicar em "Limpar", a chave atual permanece.
          </Typography>
        </Box>
      ) : null}

      {tab === 4 ? (
        <Box display="grid" gap={2} maxWidth={980}>
          <Typography variant="h6">Whisper</Typography>
          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
            <TextField label="Idioma" value={settings?.runtime?.whisper?.language || 'pt'} fullWidth disabled />
            <TextField label="WHISPER_BIN" value={settings?.runtime?.whisper?.hasWhisperBin ? 'configurado' : 'não configurado'} fullWidth disabled />
            <TextField label="WHISPER_MODEL" value={settings?.runtime?.whisper?.hasWhisperModel ? 'configurado' : 'não configurado'} fullWidth disabled />
            <TextField label="WHISPER_CPP_BIN" value={settings?.runtime?.whisper?.hasWhisperCppBin ? 'configurado' : 'não configurado'} fullWidth disabled />
            <TextField label="WHISPER_CPP_MODEL" value={settings?.runtime?.whisper?.hasWhisperCppModel ? 'configurado' : 'não configurado'} fullWidth disabled />
          </Box>

          <Typography variant="h6">FFmpeg</Typography>
          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
            <TextField label="FFMPEG_BIN" value={settings?.runtime?.ffmpeg?.ffmpegBin || 'ffmpeg'} fullWidth disabled />
            <TextField label="FFPROBE_BIN" value={settings?.runtime?.ffmpeg?.ffprobeBin || 'ffprobe'} fullWidth disabled />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
